import { Component, Input, Output, EventEmitter, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../../../core/services/settings.service';
import { ProceduresService } from '../../../../../../core/services/procedures.service';
import { firstValueFrom, debounceTime, distinctUntilChanged } from 'rxjs';
import { toast } from 'ngx-sonner';

import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { SharedModule } from 'primeng/api';
import { AppButton } from '../../../../../../shared/components/app-button/app-button';

@Component({
  selector: 'app-dtp-procedure-modal',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    TranslateModule,
    Dialog,
    SharedModule,
    InputText,
    IconField,
    InputIcon,
    AppButton
  ],
  templateUrl: './dtp-procedure-modal.html',
})
export class DtpProcedureModal implements OnInit {
  @Input() treatmentId!: number;
  @Input() branchId!: number;
  @Input() excludedProcedureIds: number[] = [];
  
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private proceduresService = inject(ProceduresService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  availableProcedures: any[] = [];
  selectedProcedures: any[] = [];
  loading = false;
  saving = false;
  searchControl = new FormControl('');

  async ngOnInit() {
    await this.loadAvailableProcedures();
    
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.filterProcedures();
    });
  }

  allProcedures: any[] = [];

  async loadAvailableProcedures() {
    this.loading = true;
    try {
      const data = await firstValueFrom(this.settingsService.getAvailableProcedures(this.branchId));
      this.allProcedures = data || [];
      this.filterProcedures();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  filterProcedures() {
    const search = (this.searchControl.value || '').toLowerCase();
    this.availableProcedures = this.allProcedures.filter(p => 
      !this.excludedProcedureIds.includes(p.id) &&
      !this.selectedProcedures.some(sp => sp.id === p.id) &&
      (p.procedure_name?.toLowerCase().includes(search) || p.description?.toLowerCase().includes(search))
    );
    this.cdr.detectChanges();
  }

  toggleProcedure(procedure: any) {
    this.selectedProcedures.push(procedure);
    this.filterProcedures();
  }

  removeSelected(procedure: any) {
    this.selectedProcedures = this.selectedProcedures.filter(p => p.id !== procedure.id);
    this.filterProcedures();
  }

  async save() {
    if (this.selectedProcedures.length === 0) return;
    this.saving = true;
    try {
      const procedureIds = this.selectedProcedures.map(p => (p as any).id);
      await firstValueFrom(this.settingsService.linkProceduresToTreatment(this.treatmentId, procedureIds, this.branchId));
      toast.success(this.translate.instant('save_success'));
      this.saved.emit();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_saving_data'));
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }
}
